import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { initFeatureFlags } from '@plentiau/fe-feature-flag-client';
import { Scope } from '@plentiau/fe-feature-flag-client/build/config';
import { FlagsByProjects } from '@plentiau/fe-feature-flag-client/build/model/flag';

import { environment } from 'environments/environment';
import { FFScope, getFeatureName } from '@brokerportal/common/enums/feature-flag';
import { Dictionary } from '../models';

@Injectable({
    providedIn: 'root'
})
export class FeatureFlagService {
    /**
     * Cache duration in seconds.
     */
    private cacheDuration = 60;
    private readonly localCache = new Map<string, { value: Dictionary<boolean>; expiry: number }>();

    constructor() {}

    /**
     * @deprecated Consider using `isEnabled('PlCarNewStatusDisplay')`.
     */
    PlCarNewStatusDisplay() {
        return environment.features.PlCarNewStatusDisplay;
    }

    /**
     * @deprecated Consider using `isEnabled('EnableEditBorrowerInfo')`.
     */
    EnableEditBorrowerInfo() {
        return environment.features.EnableEditBorrowerInfo;
    }

    /**
     * @deprecated Consider using `isEnabled('EnableIdentityAsLoanCondition')`.
     */
    EnableIdentityAsLoanCondition() {
        return environment.features.EnableIdentityAsLoanCondition;
    }

    private init(subscope: string | undefined | null = undefined) {
        // Passed as its own { project, subscope } entry (rather than baked into the
        // `scope` string) so the library's own per-project fallback kicks in: if a
        // broker doesn't have a dedicated subscope file, it falls back to the base
        // BrokerPortal flags instead of failing the whole fetch. See getFile in
        // @plentiau/fe-feature-flag-client/services/s3-service.
        const brokerPortalEntry = subscope ? { project: FFScope.BrokerPortal, subscope } : FFScope.BrokerPortal;

        return new Observable<FlagsByProjects | null>(observer => {
            initFeatureFlags(
                [brokerPortalEntry, FFScope.Venus],
                environment.featureScope as Scope,
                undefined,
                undefined,
                error => {
                    console.warn('Feature Flag init failed: ', error);
                    observer.error(error);
                },
                flags => {
                    console.debug('Feature Flags init successfully.');
                    observer.next(flags);
                    observer.complete();
                }
            );
        });
    }

    isEnabled(
        featureCode: string,
        opts: { overrideWithLocal?: boolean; useCache?: boolean } = { overrideWithLocal: true, useCache: true }
    ) {
        return this.isEnabledInSubscope(featureCode, null, opts);
    }

    /**
     * Reads a flag scoped to a specific broker subscope (e.g. `broker-{brokerGuid}`, matching
     * the application's associated broker — see `application.broker.guid` in the detail view),
     * falling back to the base BrokerPortal value for any code the broker doesn't override.
     *
     * Each distinct subscope gets its own short-lived cache entry and triggers a full,
     * independent `initFeatureFlags` fetch — deliberately not layered via the library's
     * `addFeatureFlagSubscope`, since that merges into a single shared `flagsByProjects`
     * entry per project. In a session where more than one application (and therefore more
     * than one broker) can be viewed without a full page reload, that merge would leak a
     * previously viewed broker's overrides into the next one.
     */
    isEnabledInSubscope(
        featureCode: string,
        subscope: string | undefined | null,
        { overrideWithLocal = true, useCache = true }: { overrideWithLocal?: boolean; useCache?: boolean } = {}
    ) {
        return this.get(useCache, subscope).pipe(
            map(flags => {
                const featureName = getFeatureName(featureCode);
                const scopedFeatureName = featureName + (subscope ? `-${subscope}` : '');
                const isEnabledLocal = environment.features[scopedFeatureName];
                if (overrideWithLocal !== false && typeof isEnabledLocal === 'boolean') return isEnabledLocal;
                return typeof flags[featureCode] === 'boolean' ? flags[featureCode] : !!isEnabledLocal;
            }),
            catchError((err, caught) => {
                console.warn(err, caught);
                return of(false);
            })
        );
    }

    private get(useCache = true, subscope?: string | null) {
        const cacheKey = this.getCacheKey(subscope);
        const cacheValue = this.getCache(cacheKey);
        if (!cacheValue || useCache === false) {
            return this.init(subscope).pipe(
                catchError((err, caught) => {
                    console.warn('Error occurred while initializing Feature Flags: ', err);
                    return throwError(err);
                }),
                map((flagsByProjects: FlagsByProjects | null) => {
                    if (!flagsByProjects) return null;

                    const brokerPortalFlags = (flagsByProjects[FFScope.BrokerPortal] || []).reduce<Dictionary<boolean>>(
                        (acc, cur) => ({ ...acc, [cur.code]: cur.isEnabled }),
                        {}
                    );

                    const venusFlags = (flagsByProjects[FFScope.Venus] || []).reduce<Dictionary<boolean>>(
                        (acc, cur) => ({ ...acc, [cur.code]: cur.isEnabled }),
                        {}
                    );

                    return { ...brokerPortalFlags, ...venusFlags };
                }),
                tap(dict => (dict ? this.setCache(cacheKey, dict) : void 0))
            );
        }

        return of(cacheValue);
    }

    private getCacheKey(subscope?: string | null) {
        const scope = subscope ? `${environment.featureScope}-${subscope}` : environment.featureScope;
        return `features-${FFScope.BrokerPortal}-${scope}`;
    }

    private getCache(cacheKey: string): Dictionary<boolean> | null {
        const cache = this.localCache.get(cacheKey);
        const expiryTime = cache?.expiry;
        const isExpired = typeof expiryTime === 'number' ? new Date().getTime() >= expiryTime : true;
        return isExpired ? null : cache?.value;
    }

    private setCache(cacheKey: string, data: Dictionary<boolean>) {
        this.localCache.set(cacheKey, { value: data, expiry: Date.now() + this.cacheDuration * 1000 });
    }
}

export const isEnabledOverwriteFunc = (project: string, flagCode: string): boolean => {
    if (project === FFScope.BrokerPortal) {
        return !!environment.features[getFeatureName(flagCode)];
    }

    return false;
};
